import { TListingProduct } from "@/app/api/listing/[slug]/products/listing-products.dto";
import {
	ProductTagsSectionInCard,
	showProductDetailsModal,
} from "@/components/common/product-listing-card/product-listing-card";
import UpVoteButton from "@/components/common/upvote-button";
import { useProductEngagement } from "@/hooks/useProductEngagement";
import ProductListingLoadingState from "@/modules/home/components/product-listing/loading-state";
import { useModal } from "@/providers/modal-provider";
import { CheckCircle2Icon, ImageIcon, MapPinIcon } from "lucide-react";
import Image from "next/image";

const ListViewProductCard = (props: TListingProduct) => {
	const {
		logo,
		name,
		slug,
		description,
		features,
		hqLocation,
		tags,
		tagline,
		id,
		industriesServed,
	} = props;
	const { rating, reviewsCount, isLoading, votesData, commentsCount } = useProductEngagement(slug);

	const { openModal } = useModal();

	if (isLoading) return <ProductListingLoadingState />;

	return (
		<div
			className="rounded-md border bg-white shadow-sm"
			onClick={() => showProductDetailsModal(name, slug, openModal)}
		>
			<div className="grid grid-cols-1 sm:grid-cols-5">
				<div className="sm:col-span-4 md:border-r">
					<div className="flex items-start gap-3 px-6 py-4 max-md:flex-wrap">
						<div className="aspect-square h-20 w-20 rounded-lg border">
							{logo ? (
								<Image
									src={logo}
									alt={name}
									width={80}
									height={80}
									className="h-full w-full rounded-md object-contain"
								/>
							) : (
								<ImageIcon className="h-5 w-5 text-gray-400" />
							)}
						</div>

						<div className="text-left">
							<button className="mb-1 inline-block text-left font-bold leading-tight text-gray-900 md:text-xl md:leading-normal">
								{name} <span className="text-sm text-slate-500">| {tagline}</span>
							</button>
							<p className="line-clamp-3 text-left text-sm text-gray-500">{description}</p>

							<div className="mt-3 flex flex-wrap items-center justify-start gap-x-6 gap-y-2">
								{features?.slice(0, 4).map((feature) => (
									<div key={feature} className="flex items-center justify-start gap-1">
										<span>
											<CheckCircle2Icon className="fill-brand-primary stroke-white" />
										</span>
										<span className="text-xs text-gray-500">{feature}</span>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="flex items-center justify-between gap-4 overflow-auto border-t px-6 py-4">
						<ProductTagsSectionInCard
							tags={tags?.map((tag) => ({
								name: tag.name,
								type: "tag",
							}))}
							industriesServed={industriesServed}
							commentsCount={commentsCount}
						/>
					</div>
				</div>

				<div className="flex flex-col items-center justify-center gap-3 p-4 sm:col-span-1">
					<UpVoteButton
						id={id}
						name={name}
						slug={slug}
						upVotes={votesData?.data.score || 0}
						rating={rating || "0"}
						reviewsCount={reviewsCount || 0}
						isUpvoted={votesData?.data.isUpvotedByUser ?? false}
						keyToInvalidate={["votes", slug]}
					/>

					<div className="rounded-lgp-2 hidden items-center gap-1 text-sm leading-4 text-gray-400 md:flex">
						<MapPinIcon className="h-3 w-3 text-blue-600" />
						{hqLocation}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ListViewProductCard;
